import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  useValidateSeedImport,
  useCommitSeedImport,
} from '@/hooks/use-seeds';
import type {
  ImportFormat,
  ImportConflictStrategy,
  SeedImportReport,
} from '@/types/seeds';
import { AlertCircle, CheckCircle2, AlertTriangle, FileDown } from 'lucide-react';

export interface ImportSeedsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ImportSeedsDialog({ open, onOpenChange, onSuccess }: ImportSeedsDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState<ImportFormat>('csv');
  const [report, setReport] = useState<SeedImportReport | null>(null);
  const [strategy, setStrategy] = useState<ImportConflictStrategy>('skip');
  const [skipInvalid, setSkipInvalid] = useState(true);
  const validateMutation = useValidateSeedImport();
  const commitMutation = useCommitSeedImport();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setReport(null);
    if (f) {
      if (f.name.toLowerCase().endsWith('.xlsx')) setFormat('xlsx');
      else setFormat('csv');
    }
  };

  const handleValidate = async () => {
    if (!file) {
      toast.error('Choose a file first');
      return;
    }
    try {
      const r = await validateMutation.mutateAsync({ file, format });
      setReport(r);
      toast.success(`Validated ${r.total_rows} rows: ${r.ok_count} ok, ${r.warning_count} warnings, ${r.error_count} errors`);
    } catch (e) {
      toast.error(`Validation failed: ${(e as Error).message}`);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    try {
      const summary = await commitMutation.mutateAsync({
        file,
        format,
        strategy,
        skipInvalid,
      });
      toast.success(
        `Import committed: ${summary.created} created, ${summary.updated} updated, ${summary.skipped} skipped, ${summary.failed} failed`,
      );
      reset();
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(`Import failed: ${(e as Error).message}`);
    }
  };

  const reset = () => {
    setFile(null);
    setReport(null);
    setStrategy('skip');
    setSkipInvalid(true);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import seeds</DialogTitle>
          <DialogDescription>
            Upload a CSV or XLSX file. We will validate every row before you commit. The file
            must contain columns: problem_category, seed_number, title, description,
            estimated_minutes, difficulty_level.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-dashed bg-muted/30 p-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="font-medium">Need a template?</div>
                <div className="text-xs text-muted-foreground">
                  Download the example CSV, fill it with your seeds, and upload it here.
                </div>
              </div>
              <a
                href="/seeds-import-example.csv"
                download
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                <FileDown className="mr-2 h-4 w-4" />
                Example CSV
              </a>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="import-file">File</Label>
              <Input
                id="import-file"
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileChange}
              />
            </div>
            <div className="space-y-1">
              <Label>Format</Label>
              <Select value={format} onValueChange={(v) => setFormat((v as ImportFormat) ?? 'csv')}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xlsx">XLSX</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={handleValidate}
              disabled={!file || validateMutation.isPending}
            >
              {validateMutation.isPending ? 'Validating…' : 'Validate'}
            </Button>
          </div>

          {report && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" /> OK {report.ok_count}
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <AlertTriangle className="h-3 w-3" /> Warnings {report.warning_count}
                </Badge>
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" /> Errors {report.error_count}
                </Badge>
              </div>

              {report.rows.length > 0 && (
                <div className="max-h-60 overflow-y-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 text-left">Row</th>
                        <th className="px-2 py-1 text-left">Status</th>
                        <th className="px-2 py-1 text-left">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.slice(0, 100).map((r) => (
                        <tr key={r.row_number} className="border-t">
                          <td className="px-2 py-1">{r.row_number}</td>
                          <td className="px-2 py-1">
                            <Badge
                              variant={
                                r.status === 'ok'
                                  ? 'default'
                                  : r.status === 'warning'
                                  ? 'secondary'
                                  : 'destructive'
                              }
                            >
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-2 py-1">{(r.messages ?? []).join('; ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="space-y-1">
                  <Label>Conflict strategy</Label>
                  <Select
                    value={strategy}
                    onValueChange={(v) => setStrategy((v as ImportConflictStrategy) ?? 'skip')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="skip">Skip conflicts</SelectItem>
                      <SelectItem value="update">Update conflicts</SelectItem>
                      <SelectItem value="fail">Fail on conflict</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={skipInvalid}
                      onCheckedChange={(c) => setSkipInvalid(!!c)}
                    />
                    Skip invalid rows
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            disabled={!report || commitMutation.isPending}
          >
            {commitMutation.isPending ? 'Committing…' : 'Commit import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
